// The Sources block of the context list and the top bar Sources chip: one
// text row per source route, derived from GET /api/beta/source-connectors
// (api-surface 2.7). Five connector kinds are routes; a kind with no
// connector row is "not configured". Presence: live when a connected route
// has evidence or a heartbeat inside the freshness window; stale when it is
// connected but the newest evidence is older than that (an import with no
// heartbeat, as on live in August 2026); off for pending, revoked or absent;
// error for the error status. Nothing here invents a count.
import type { ConnectorKind, SourceConnector, SourceConnectorsResponse } from '../api/types';
import type { Presence, SourceFamily } from '../ui/PresenceDot';

export const SOURCE_FRESH_WINDOW_MS = 6 * 60 * 60 * 1000;

export interface SourceRoute {
  readonly kind: ConnectorKind;
  readonly label: string;
  readonly family: SourceFamily;
  readonly presence: Presence;
  readonly meta: string;
  readonly reason: string;
}

const ROUTE_ORDER: ReadonlyArray<{ readonly kind: ConnectorKind; readonly label: string; readonly family: SourceFamily }> = [
  { kind: 'telegram_tdlib', label: 'Telegram reader', family: 'telegram' },
  { kind: 'telegram_bot', label: 'Telegram bot', family: 'telegram' },
  { kind: 'telegram_public', label: 'Public collector', family: 'telegram' },
  { kind: 'discord', label: 'Discord', family: 'discord' },
  { kind: 'x', label: 'X', family: 'x' },
];

export const SOURCE_ROUTE_COUNT = ROUTE_ORDER.length;

function newestInstant(connector: SourceConnector): number | null {
  const candidates = [connector.lastEvidenceAt, connector.lastEventAt, ...connector.channels.map((channel) => channel.lastEvidenceAt)]
    .map((value) => (value ? Date.parse(value) : Number.NaN))
    .filter((value) => Number.isFinite(value));
  return candidates.length ? Math.max(...candidates) : null;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function describeConnector(connector: SourceConnector, now: number): Pick<SourceRoute, 'presence' | 'meta' | 'reason'> {
  const channels = connector.channels.filter((channel) => channel.selected).length;
  const channelText = plural(channels, 'channel');
  switch (connector.status) {
    case 'connected': {
      const newest = newestInstant(connector);
      if (newest !== null && now - newest <= SOURCE_FRESH_WINDOW_MS) {
        return { presence: 'live', meta: channelText, reason: 'Connected with evidence inside the last 6 hours.' };
      }
      return {
        presence: 'stale',
        meta: channelText,
        reason: newest === null
          ? 'Connected, but no evidence or heartbeat has been recorded on this route.'
          : 'Connected, but the newest evidence is older than 6 hours.',
      };
    }
    case 'degraded':
      return { presence: 'stale', meta: 'degraded', reason: `Connector degraded${connector.lastErrorCode ? ` (${connector.lastErrorCode})` : ''}.` };
    case 'error':
      return { presence: 'error', meta: 'error', reason: `Connector reported an error${connector.lastErrorCode ? ` (${connector.lastErrorCode})` : ''}.` };
    case 'pending':
      return { presence: 'off', meta: 'pending', reason: 'Connector installed but not yet confirmed.' };
    case 'revoked':
      return { presence: 'off', meta: 'revoked', reason: 'Connector was revoked; reconnect to resume.' };
    default:
      return { presence: 'off', meta: String(connector.status), reason: `Unrecognised connector status ${String(connector.status)}.` };
  }
}

export function deriveSourceRoutes(response: SourceConnectorsResponse | undefined, now: number = Date.now()): ReadonlyArray<SourceRoute> {
  return ROUTE_ORDER.map((route) => {
    const connector = response?.connectors.find((candidate) => candidate.connectorKind === route.kind);
    if (!connector) {
      const publicCollector = route.kind === 'telegram_public' ? response?.telegramPublicCollector : null;
      if (publicCollector) {
        const fresh = publicCollector.fresh && publicCollector.lifecycle === 'running';
        return {
          ...route,
          presence: fresh ? 'live' : 'stale',
          meta: plural(publicCollector.trackedSources, 'source'),
          reason: `Public collector ${publicCollector.lifecycle}${publicCollector.fresh ? '' : ', heartbeat not fresh'}.`,
        };
      }
      return { ...route, presence: 'off', meta: 'not configured', reason: 'No connector of this kind is configured for the workspace.' };
    }
    return { ...route, ...describeConnector(connector, now) };
  });
}

export interface SourcesSummary {
  readonly presence: Presence;
  readonly text: string;
  readonly detail: string;
  readonly connected: number;
}

// The top bar chip line: "Sources: 1 of 5 routes, Telegram reader stale".
export function summariseSources(routes: ReadonlyArray<SourceRoute>): SourcesSummary {
  const active = routes.filter((route) => route.presence === 'live' || route.presence === 'stale');
  const stale = active.filter((route) => route.presence === 'stale');
  const errored = routes.filter((route) => route.presence === 'error');
  const detail = routes.map((route) => `${route.label}: ${route.meta}`).join('; ');
  if (errored.length) {
    return { presence: 'error', connected: active.length, detail, text: `Sources: ${errored[0]?.label ?? 'a route'} in error, ${active.length} of ${routes.length} routes` };
  }
  if (active.length === 0) {
    return { presence: 'off', connected: 0, detail, text: `Sources: setup required, 0 of ${routes.length} routes` };
  }
  if (stale.length === active.length) {
    return { presence: 'stale', connected: active.length, detail, text: `Sources: ${active.length} of ${routes.length} routes, ${stale[0]?.label ?? 'route'} stale` };
  }
  return { presence: 'live', connected: active.length, detail, text: `Sources: ${active.length} of ${routes.length} routes live` };
}
